import { Money, formatDate, formatMoney } from '@betterment/core';
import { token } from '@betterment/samba-styled-system/tokens';
import { isToday } from 'date-fns';

import { useXYScales } from '../axis/useXYScales';
import { XAxis } from '../axis/XAxis';
import { YAxis } from '../axis/YAxis';
import { ChartProvider } from '../ChartProvider';
import { Cursor } from '../cursor/Cursor';
import { Grid } from '../Grid';
import { Legend } from '../Legend';
import { type BalanceChartData, BalanceChartDataSeries } from '../series/BalanceSeries';
import { XYChart, type XYChartProps } from '../XYChart';

type BalanceChartProps = Omit<XYChartProps, 'children' | 'width' | 'height'> & {
  children?: React.ReactNode;
  data: BalanceChartData;
  width: number;
  height: number;
};

const formatDateLabel = (value: Date) => {
  return isToday(value) ? 'Today' : formatDate(value, 'longOrdinalShortMonth');
};

const yTickFormat = (value: number) => formatMoney(Money(value), 'compact');

const formatBalanceDataPointLabel = (datum: BalanceChartData[0], index: number, total: number): string => {
  if (!datum) return '';

  const dateStr = formatDate(datum.date, 'longOrdinalShortMonth');
  const balanceStr = formatMoney(datum.balance);
  const investedStr = formatMoney(datum.invested);

  return `Point ${index + 1} of ${total}, ${dateStr}, Balance: ${balanceStr}, Invested: ${investedStr}`;
};

export const BalanceChart = ({
  children,
  margin = { right: 0, top: 10, left: 50, bottom: 20 },
  theme,
  data,
  width,
  height,
  ...chartProps
}: BalanceChartProps) => {
  const allValues = data.flatMap(d => [d.balance.cents, d.invested.cents]);
  const dataMin = Math.min(...allValues);
  const dataMax = Math.max(...allValues);

  const { xScale, yScale } = useXYScales({
    yMin: dataMin,
    yMax: dataMax,
  });

  const balanceDataKey = 'Balance';
  const investedDataKey = 'Invested';

  return (
    <ChartProvider xScale={xScale} yScale={yScale} theme={theme || { colors: [token('colors.dataviz.blue.2')] }}>
      <Legend
        config={{
          [balanceDataKey]: { marker: { shape: 'line', variant: 'solid' } },
          [investedDataKey]: { marker: { shape: 'line', variant: 'dashed' } },
        }}
      />
      <XYChart width={width} height={height} margin={margin} {...chartProps}>
        <Grid />
        <XAxis tickFormat={formatDateLabel} />
        <YAxis tickFormat={yTickFormat} />
        <BalanceChartDataSeries data={data} balanceDataKey={balanceDataKey} investedDataKey={investedDataKey} />
        <Cursor formatDataPointLabel={formatBalanceDataPointLabel} />
        {children}
      </XYChart>
    </ChartProvider>
  );
};
